import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Target, Trophy } from 'lucide-react';
import { budgetService, type Budget } from '../../services/budgetService';
import { useAuth } from '../../context/AuthContext';

/**
 * Miniatura "Budget" in navbar (utente loggato), tra le voci principali e
 * "Outlet" (richiesta utente 21/02/2026). Riusa `/api/clienti/me/budget`
 * (stessa fonte dati della card "Il tuo Budget" in Area Cliente) per
 * mostrare una barra di completamento compatta con % raggiunta.
 *
 * Nascosta (return null) se il budget non è abilitato per il cliente
 * (agenti/admin non collegati a un an_conto, o periodo "expired") — stesso
 * criterio già usato da `BudgetCard`.
 */
export function BudgetNavBadge({ mobile = false }: { mobile?: boolean }) {
  const location = useLocation();
  const { hasRole } = useAuth();
  // Il budget è del Cliente: per admin, backoffice e agenti la chiamata
  // risponde 403, e la si faceva a ogni pagina (audit RMA #26).
  const isCliente = hasRole('cliente', 'cliente_subordinato');
  const [budget, setBudget] = useState<Budget | null>(null);

  useEffect(() => {
    if (!isCliente) return;
    let cancelled = false;
    budgetService.getMine().then(data => { if (!cancelled) setBudget(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [isCliente]);

  if (!budget || !budget.enabled) return null;
  if (budget.status !== 'pre-target' && budget.status !== 'target-reached') return null;

  const reached = budget.status === 'target-reached';
  const pct = reached ? 100 : Math.min(100, Math.max(0, budget.progressoVersoTarget));
  const active = location.pathname.startsWith('/area-clienti');
  const title = reached
    ? `Sconto ${budget.percentuale}% attivo sul tuo budget`
    : `Ti mancano ${budget.targetMancante.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })} per sbloccare lo sconto ${budget.percentuale}%`;

  if (mobile) {
    return (
      <Link
        to="/area-clienti"
        title={title}
        data-testid="mobile-nav-budget"
        className="flex items-center gap-3 h-12 px-3 rounded-lg border-2 border-[#2B5EA7] mb-2 hover:bg-[#2B5EA7]/5 transition-colors"
      >
        {reached ? <Trophy className="w-4 h-4 text-emerald-500 flex-shrink-0" /> : <Target className="w-4 h-4 text-[#2B5EA7] flex-shrink-0" />}
        <span className="text-sm font-semibold text-[#2B5EA7]">Budget</span>
        <span className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
          <span className={`block h-full rounded-full ${reached ? 'bg-emerald-500' : 'bg-amber-400'}`} style={{ width: `${pct}%` }} />
        </span>
        <span className="text-xs font-bold text-[#2B5EA7] tabular-nums">{Math.round(pct)}%</span>
      </Link>
    );
  }

  return (
    <Link
      to="/area-clienti"
      data-testid="primary-nav-budget"
      className={`group relative flex items-center gap-2 px-3 py-1.5 rounded-md border transition-colors ${
        active ? 'bg-white/20 border-white/50' : 'border-white/30 hover:border-white/50 hover:bg-white/10'
      }`}
    >
      {reached ? <Trophy className="w-3.5 h-3.5 text-emerald-300" /> : <Target className="w-3.5 h-3.5 text-amber-400" />}
      <span className="text-sm font-medium text-white/90">Budget</span>
      <span className="relative w-10 h-1.5 rounded-full bg-white/25 overflow-hidden">
        <span
          className={`absolute inset-y-0 left-0 rounded-full transition-all ${reached ? 'bg-emerald-400' : 'bg-amber-400'}`}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="text-xs font-bold text-white tabular-nums">{Math.round(pct)}%</span>

      {/* Mini popup al hover — sostituisce il tooltip nativo del browser
          (richiesta utente 21/02/2026: stile custom, coerente col sito) */}
      <div
        role="tooltip"
        data-testid="primary-nav-budget-tooltip"
        className="absolute top-full right-0 mt-2 whitespace-nowrap px-3 py-1.5 rounded-lg bg-[hsl(214,58%,41%)] text-white text-xs shadow-lg opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150 pointer-events-none z-[110]"
      >
        {title}
      </div>
    </Link>
  );
}
